import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Breadcrumb from 'base/components/Breadcrumb';
import Flex from 'base/components/Flex';
import { openModal } from 'modal/index';
import { getActiveSpace } from 'spaces/index';
import { getIsAdmin } from 'auth/store/auth/selectors';
import { getUsersById } from 'auth/store/users/selectors';
import { getActiveFolder, getFoldersById } from '../../store/folders/selectors';
import { setEditingFolderId } from '../../store/folders/actions';
import {
  fetchDocumentsRecursiveByFolderPath,
  setActiveDocumentId,
} from '../../store/documents/actions';
import { getActiveFolderRecursiveDocuments } from '../../store/documents/selectors';
import FileExplorer from '../../components/FileExplorer/FileExplorer';
import { getTagsById } from '../../store/tags/selectors';
import { getFavoriteIds, addFavorite, removeFavorite, fetchFavorites } from '../../../favorites';
import FileExplorerBulkActions from '../FileExplorerBulkActions';
import { fetchAllTags } from '../../store/tags/actions';
import DocumentLink from '../DocumentLink/DocumentLink';
import {
  cacheDocumentOffline,
  getOfflineDocumentIds,
  uncacheDocumentOffline,
} from '../../../offline';
import { setSelectedData } from '../../store/fileexplorer/actions';


class RecursiveFileExplorer extends React.Component {
  componentDidMount() {
    this.fetchData();
    this.props.fetchFavorites();
    this.props.fetchAllTags();
  }

  componentDidUpdate(prevProps) {
    const spaceChanged =
        // initial load
        (!prevProps.space && this.props.space)
        // space changed
        || (prevProps.space && this.props.space && prevProps.space.id !== this.props.space.id);
    const folderChanged =
        // initial load or switch from 'documents' to specific folder
        (!prevProps.folder && !!this.props.folder)
        // switch from specific folder to all 'documents'
        || (!!prevProps.folder && !this.props.folder)
        // switch between folders
        || (prevProps.folder && this.props.folder
            && prevProps.folder.id !== this.props.folder.id);
    if (spaceChanged || folderChanged) {
      this.fetchData();
    }
  }

  getBulkActions = ({ selectedData }) => (<FileExplorerBulkActions selectedData={selectedData} />);

  fetchData = () => {
    if (!this.props.space) {
      return;
    }
    const spaceId = this.props.space.id;
    const folderPath = this.props.folder && this.props.folder.path;
    this.props.fetchDocumentsRecursiveByFolderPath(spaceId, folderPath);
  };

  isFolder = data => data.parent !== undefined;

  handleEdit = (data) => {
    if (this.isFolder(data)) {
      this.props.setEditingFolderId(data.id);
      this.props.openModal('FOLDER_EDIT');
    } else {
      this.props.setActiveDocumentId(data.id);
      this.props.openModal('DOCUMENT_EDIT');
    }
  };

  handleDefaultFavoriteEdit = (data) => {
    this.props.setActiveDocumentId(data.id);
    this.props.openModal('DOCUMENT_DEFAULT_FAVORITES_EDIT');
  };

  handleDelete = (data) => {
    if (this.isFolder(data)) {
      this.props.setEditingFolderId(data.id);
      this.props.openModal('FOLDER_DELETE');
    } else {
      this.props.setActiveDocumentId(data.id);
      this.props.openModal('DOCUMENT_DELETE');
    }
  };

  handleFavoriteToggle = (data) => {
    if (data.entityType === 'document') {
      const { favoriteIds, addFavorite, removeFavorite } = this.props;
      if (favoriteIds.includes(data.id)) {
        removeFavorite(data);
      } else {
        addFavorite(data);
      }
    }
  };

  handleCacheOfflineToggle = (data) => {
    if (data.entityType === 'document') {
      const { offlineDocumentIds, cacheDocumentOffline, uncacheDocumentOffline } = this.props;
      if (offlineDocumentIds.includes(data.id)) {
        uncacheDocumentOffline(data);
      } else {
        cacheDocumentOffline(data);
      }
    }
  };

  handleShare = (data) => {
    this.props.setSelectedData([data]);
    this.props.openModal('DOCUMENT_SHARE');
  };

  NameCell = ({ original }) => { // eslint-disable-line
    const { foldersById } = this.props;
    if (!original || !original.folder) {
      return <DocumentLink document={original} />;
    }
    const folder = foldersById[original.folder];
    const paths = folder.pathIds.map(id => ({
      path: foldersById[id].route,
      label: foldersById[id].name,
    }));
    return (
      <Flex flexDirection="column">
        <Breadcrumb paths={paths} />
        <DocumentLink document={original} />
      </Flex>
    );
  };

  render() {
    const {
      documents, usersById, tagsById, isAdmin,
    } = this.props;
    return (
      <div>
        <FileExplorer
          data={documents}
          onRowEdit={this.handleEdit}
          onRowDelete={this.handleDelete}
          onRowFavoriteToggle={this.handleFavoriteToggle}
          onRowCacheOfflineToggle={this.handleCacheOfflineToggle}
          onDefaultFavoriteEdit={this.handleDefaultFavoriteEdit}
          onShare={this.handleShare}
          usersById={usersById}
          tagsById={tagsById}
          isAdmin={isAdmin}
          bulkActions={this.getBulkActions}
          defaultSorted={[{
            id: 'createdAt',
            desc: true,
          }]}
          columnsCallback={cols => cols.map(col => (col.id === 'name'
              ? ({
                ...col,
                Cell: this.NameCell,
              })
            : col))
          }
        />
      </div>

    );
  }
}
RecursiveFileExplorer.propTypes = {
  space: PropTypes.object, // eslint-disable-line
  folder: PropTypes.object, // eslint-disable-line
  usersById: PropTypes.objectOf(PropTypes.any).isRequired,
  tagsById: PropTypes.objectOf(PropTypes.any).isRequired,
  foldersById: PropTypes.objectOf(PropTypes.any).isRequired,
  documents: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchDocumentsRecursiveByFolderPath: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  setEditingFolderId: PropTypes.func.isRequired,
  setActiveDocumentId: PropTypes.func.isRequired,
  addFavorite: PropTypes.func.isRequired,
  removeFavorite: PropTypes.func.isRequired,
  favoriteIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  offlineDocumentIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  cacheDocumentOffline: PropTypes.func.isRequired,
  uncacheDocumentOffline: PropTypes.func.isRequired,
  fetchFavorites: PropTypes.func.isRequired,
  fetchAllTags: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  setSelectedData: PropTypes.func.isRequired,
};
RecursiveFileExplorer.defaultProps = {
  folder: null,
  space: null,
};

const mapStateToProps = state => ({
  usersById: getUsersById(state),
  tagsById: getTagsById(state),
  foldersById: getFoldersById(state),
  space: getActiveSpace(state),
  folder: getActiveFolder(state),
  documents: getActiveFolderRecursiveDocuments(state),
  favoriteIds: getFavoriteIds(state),
  offlineDocumentIds: getOfflineDocumentIds(state),
  isAdmin: getIsAdmin(state),
});

const mapDispatchToProps = {
  fetchDocumentsRecursiveByFolderPath,
  openModal,
  setEditingFolderId,
  setActiveDocumentId,
  addFavorite,
  removeFavorite,
  fetchFavorites,
  fetchAllTags,
  cacheDocumentOffline,
  uncacheDocumentOffline,
  setSelectedData,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RecursiveFileExplorer);

